/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Field, Input, TextArea } from '@adobe-commerce/elsie/components';
import { GiftOptionsViewProps, GiftFormDataType } from '@/cart/types';
import { useId } from 'preact/hooks';

interface FormFieldsProps {
  view: GiftOptionsViewProps;
  giftOptions: GiftFormDataType;
  disabled: boolean;
  errorMessage: Record<string, string>;
  onInputChange: (value: Event) => void;
  onBlur: (event: Event) => void;
  isGiftMessageVisible: boolean;
}

export const FormFields: FunctionComponent<FormFieldsProps> = ({
  view,
  giftOptions,
  disabled,
  errorMessage,
  onInputChange,
  onBlur,
  isGiftMessageVisible,
}) => {
  const id = useId();
  const dictionary = useText({
    formTitle: `Cart.GiftOptions.${view}.formContent.formTitle`,
    formTo: `Cart.GiftOptions.${view}.formContent.formTo`,
    formFrom: `Cart.GiftOptions.${view}.formContent.formFrom`,
    giftMessageTitle: `Cart.GiftOptions.${view}.formContent.giftMessageTitle`,
    formToPlaceholder: `Cart.GiftOptions.${view}.formContent.formToPlaceholder`,
    formFromPlaceholder: `Cart.GiftOptions.${view}.formContent.formFromPlaceholder`,
    formMessagePlaceholder: `Cart.GiftOptions.${view}.formContent.formMessagePlaceholder`,
  });

  if (!isGiftMessageVisible) return null;

  return (
    <>
      <span>{dictionary.formTitle}</span>
      <div>
        <span>{dictionary.formTo}</span>
        <Field disabled={disabled} error={errorMessage.recipientName}>
          <Input
            id={`recipientName-${id}`}
            disabled={disabled}
            type="text"
            name={'recipientName'}
            value={giftOptions.recipientName}
            placeholder={dictionary.formToPlaceholder}
            onChange={onInputChange}
            onBlur={onBlur}
          />
        </Field>
      </div>
      <div>
        <span>{dictionary.formFrom}</span>
        <Field disabled={disabled} error={errorMessage.senderName}>
          <Input
            id={`senderName-${id}`}
            disabled={disabled}
            type="text"
            name={'senderName'}
            value={giftOptions.senderName}
            placeholder={dictionary.formFromPlaceholder}
            onChange={onInputChange}
            onBlur={onBlur}
          />
        </Field>
      </div>
      <div>
        <span>{dictionary.giftMessageTitle}</span>
        <Field disabled={disabled}>
          <TextArea
            id={`message-${id}`}
            errorMessage={errorMessage.message}
            disabled={disabled}
            name={'message'}
            value={giftOptions.message}
            label={dictionary.formMessagePlaceholder}
            onChange={onInputChange}
            onBlur={onBlur}
          />
        </Field>
      </div>
    </>
  );
};
